import { Accordion, Button, NumberInput, Select, Text, Textarea } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlayerPlay } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useRouter } from 'next/router';
import { api, type RouterInputs } from '~/trpc-extra/api';

type SingleTestRunModalProps = {
  testId: string;
  projectId: string;
  testName: string;
};

type FormValues = {
  retryOnFailure: 'true' | 'false';
  maxRetries: number;
  operationDelayMs?: number | null;
  testTimeoutSeconds?: number | null;
  note: string;
};

type RunSingleTestInput = RouterInputs['test']['runSingleTest'];

function SingleTestRunModal({ testId, projectId, testName }: SingleTestRunModalProps) {
  const router = useRouter();
  const form = useForm<FormValues>({
    initialValues: {
      retryOnFailure: 'true',
      maxRetries: 0,
      operationDelayMs: null,
      testTimeoutSeconds: null,
      note: '',
    },
  });

  const utils = api.useUtils();
  const { mutateAsync: runSingleTest, isPending } = api.test.runSingleTest.useMutation();

  return (
    <form
      onSubmit={form.onSubmit(async (values) => {
        const options: Record<string, unknown> = {};

        if (values.maxRetries > 0) {
          if (values.retryOnFailure === 'true') {
            options.retries = values.maxRetries;
          } else {
            options.repeats = values.maxRetries;
          }
        }

        if (values.operationDelayMs && values.operationDelayMs > 0) {
          options.operationDelayMs = values.operationDelayMs;
        }

        if (values.testTimeoutSeconds && values.testTimeoutSeconds > 0) {
          options.testTimeoutMs = Math.floor(values.testTimeoutSeconds * 1000);
        }

        const trimmedNote = values.note.trim();
        if (trimmedNote.length > 0) {
          options.metadata = { note: trimmedNote };
        }

        const payload: RunSingleTestInput = {
          testId,
          projectId,
        };

        if (Object.keys(options).length > 0) {
          payload.options = options as NonNullable<RunSingleTestInput['options']>;
        }

        const toastId = notifications.show({
          color: 'blue',
          message: `Starting run for "${testName}"`,
          loading: true,
          autoClose: false,
        });

        modals.closeAll();

        void runSingleTest(payload)
          .then(({ testSuiteRunId }) => {
            notifications.update({
              id: toastId,
              color: 'teal',
              message: `Run queued for "${testName}"`,
              loading: false,
              autoClose: 3000,
            });

            void utils.suiteRun.list.invalidate({ projectId }).catch(() => undefined);
            void utils.test.listTestRuns.invalidate({ testId }).catch(() => undefined);

            void router.push(`/project/${projectId}/history/g_${testSuiteRunId}`);
          })
          .catch((error) => {
            const message = error instanceof Error ? error.message : 'Unable to start run. Please try again.';

            notifications.update({
              id: toastId,
              color: 'red',
              title: 'Failed to start run',
              message,
              loading: false,
              autoClose: 6000,
            });
          });
      })}
    >
      <div className="flex flex-col gap-6">
        <Text
          size="sm"
          c="dimmed"
        >
          Runs with default settings. Expand the advanced configuration if you need to tweak retries, delays, or add a
          note.
        </Text>

        <Accordion
          variant="separated"
          chevronPosition="left"
        >
          <Accordion.Item value="advanced">
            <Accordion.Control>Advanced configuration</Accordion.Control>
            <Accordion.Panel>
              <div className="flex flex-col gap-4">
                <Select
                  label="Retry behavior"
                  data={[
                    { value: 'true', label: 'Only retry on failure' },
                    { value: 'false', label: 'Always repeat the run' },
                  ]}
                  {...form.getInputProps('retryOnFailure')}
                />
                <NumberInput
                  label="Retries / repeats"
                  description="Number of retries or repeats to apply"
                  allowNegative={false}
                  min={0}
                  {...form.getInputProps('maxRetries')}
                />
                <NumberInput
                  label="Action delay (ms)"
                  description="Optional delay between actions"
                  allowNegative={false}
                  min={0}
                  {...form.getInputProps('operationDelayMs')}
                />
                <NumberInput
                  label="Test timeout (seconds)"
                  description="Maximum duration for the test. Leave empty to use default (1200 seconds / 20 minutes)"
                  placeholder="1200 (default)"
                  allowNegative={false}
                  min={0}
                  {...form.getInputProps('testTimeoutSeconds')}
                />
                <Textarea
                  label="Run note"
                  description="Optional context for this run"
                  autosize
                  minRows={2}
                  {...form.getInputProps('note')}
                />
              </div>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        <div className="flex flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => modals.closeAll()}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            leftSection={<IconPlayerPlay size={16} />}
            loading={isPending}
          >
            Start run
          </Button>
        </div>
      </div>
    </form>
  );
}

export function openSingleTestRunModal(props: SingleTestRunModalProps & { zIndex?: number }) {
  modals.open({
    title: <span className="text-base font-semibold">Run {props.testName}</span>,
    size: 'lg',
    children: <SingleTestRunModal {...props} />,
    zIndex: props.zIndex,
  });
}
